// API Response Types matching mobile app

export type UserResponse = {
  id: number;
  created_at: string;
  clerk_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone_number: string | null;
  date_of_birth: string | null;
  stripe_customer_id: string | null;
  subscribed: boolean;
  expo_push_token: string | null;
};

export type HouseholdMemberResponse = {
  user_id: number;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone_number?: string | null;
};

export type BeneficiaryResponse = {
  beneficiary_id: number;
  name: string;
  type: 'CHILD' | 'PET' | 'OTHER';
  birthdate: string | null;
};

export type HouseholdResponse = {
  id: number;
  members: HouseholdMemberResponse[];
  beneficiaries: BeneficiaryResponse[];
};

export type InviteResponse = {
  id: number;
  created_at: string;
  from_user_id: number;
  to_name: string;
  to_phone_number: string;
  household_id: number;
  expiration_date: string;
  accepted_at: string | null;
  rejected_at: string | null;
  accepted_user_id: number | null;
  rejected_user_id: number | null;
  from_user: {
    id: number;
    created_at: string;
    first_name: string | null;
    last_name: string | null;
  } | null;
};

export type InviteListResponse = {
  received: InviteResponse[];
  sent: InviteResponse[];
};

export type ProfessionalInviteResponse = {
  id: number;
  created_at: string;
  coparent_user_id: number;
  household_id: number;
  professional_email: string;
  professional_user_id: number | null;
  status:
    | 'pending'
    | 'accepted'
    | 'rejected'
    | 'revoked'
    | 'pending_coparent_share'
    | 'dismissed';
  share_chats: boolean;
  share_expenses: boolean;
  share_custody_calendar: boolean;
  share_journal: boolean;
  accepted_at: string | null;
  rejected_at: string | null;
  revoked_at: string | null;
  coparent_name: string | null;
  professional_name: string | null;
};

export type ProfessionalInviteListResponse = {
  invites: ProfessionalInviteResponse[];
};

export type ProfessionalClientListResponse = {
  clients: ProfessionalInviteResponse[];
};

export type ProfessionalInviteCode = {
  id: number;
  code: string;
  status: 'available' | 'redeemed' | 'revoked' | 'expired';
  created_at: string;
  expires_at: string | null;
  redeemed_at: string | null;
  click_count: number;
  redeemed_by_user_id: number | null;
  redeemed_household_id: number | null;
};

export type ProfessionalCodeBatch = {
  id: number;
  created_at: string;
  updated_at: string;
  professional_user_id: number;
  label: string | null;
  grant_source: string;
  trial_days: number;
  total_codes: number;
  available_count: number;
  redeemed_count: number;
  revoked_count: number;
  expired_count: number;
  expires_at: string | null;
  default_share_chats: boolean;
  default_share_expenses: boolean;
  default_share_custody_calendar: boolean;
  default_share_journal: boolean;
};

export type ProfessionalCodeBatchListResponse = {
  batches: ProfessionalCodeBatch[];
};

export type ProfessionalCodeBatchDetailResponse = {
  batch: ProfessionalCodeBatch;
  codes: ProfessionalInviteCode[];
};

export type ProfessionalCodeBatchCreateRequest = {
  count: number;
  label?: string;
  trial_days: number;
  default_share_chats: boolean;
  default_share_expenses: boolean;
  default_share_custody_calendar: boolean;
  default_share_journal: boolean;
  expires_at?: string | null;
};

export type ProfessionalCodePreviewResponse = {
  code: string;
  status: string;
  trial_days: number;
  professional_user_id: number;
  professional_name: string | null;
  default_share_chats: boolean;
  default_share_expenses: boolean;
  default_share_custody_calendar: boolean;
  default_share_journal: boolean;
  expires_at: string | null;
  valid: boolean;
};

export type ProfessionalCodeRedeemResponse = {
  code: string;
  status: string;
  trial_days: number;
  trial_expires_at: string;
  pending_connection_id: number;
  professional_name: string | null;
};

export type PendingProfessionalConnection = {
  invite_id: number;
  professional_user_id: number;
  professional_name: string | null;
  professional_email: string;
  trial_days: number;
  from_code: string;
  created_at: string;
  share_chats: boolean;
  share_expenses: boolean;
  share_custody_calendar: boolean;
  share_journal: boolean;
};

export type PendingProfessionalConnectionListResponse = {
  connections: PendingProfessionalConnection[];
};

export type ProfessionalDirectoryProfileStatus =
  | 'pending'
  | 'approved'
  | 'rejected';

export type ProfessionalDirectoryProfile = {
  id: number;
  professional_user_id: number;
  display_name: string;
  headline: string | null;
  bio: string;
  state: string;
  specialties: string[];
  website_url: string;
  email_public: string | null;
  status: ProfessionalDirectoryProfileStatus;
  submitted_at: string;
  reviewed_at: string | null;
  reviewed_by_user_id: number | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
};

export type ProfessionalDirectoryProfileListResponse = {
  profiles: ProfessionalDirectoryProfile[];
};

export type PublicCoachProfile = {
  id: number;
  display_name: string;
  headline: string | null;
  bio: string;
  state: string;
  specialties: string[];
  website_url: string;
  email_public: string | null;
  created_at: string;
};

export type PublicCoachProfileListResponse = {
  coaches: PublicCoachProfile[];
};

export type ProfessionalClientChatListItem = {
  id: number;
  title: string;
  is_archived: boolean;
  created_at: string;
  message_count: number;
  last_message_timestamp: string | null;
};

export type ProfessionalClientChatListResponse = {
  chats: ProfessionalClientChatListItem[];
};

// Utility type for invite status
export function getInviteStatus(
  invite: InviteResponse
): 'pending' | 'accepted' | 'rejected' {
  if (invite.accepted_user_id) return 'accepted';
  if (invite.rejected_user_id) return 'rejected';
  return 'pending';
}

// Get the name of the user who sent an invite
export function getFromUserName(invite: InviteResponse): string {
  if (invite.from_user) {
    const firstName = invite.from_user.first_name || '';
    const lastName = invite.from_user.last_name || '';
    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }
  }
  return invite.to_name || 'Someone';
}

// Get member display name
export function getMemberName(member: HouseholdMemberResponse): string {
  if (member.first_name && member.last_name) {
    return `${member.first_name} ${member.last_name}`;
  }
  if (member.first_name) {
    return member.first_name;
  }
  return 'Unknown';
}

// Accounting types
export type AccountingSummary = {
  amount_owed_cents: number;
  my_user_share_of_other_user_purchases?: number;
  other_user_share_of_my_user_purchases?: number;
  my_user_total_payments?: number;
  other_user_total_payments?: number;
};

// Expense types
export type ExpenseCategory = {
  expense_category_id: string;
  name: string;
  color: string;
};

export type ExpenseCategoryListResponse = {
  categories: ExpenseCategory[];
};

export type ExpenseAttachment = {
  id: number;
  creator_user_id: number;
  expense_id: number;
  attachment_type: string;
  name: string;
  slug: string;
  created_at: string;
  download_url: string;
};

export type ExpenseSplit = {
  id: number;
  split_for_user_id: number;
  amount_cents: number;
  amount_percent: number;
};

export type ExpenseDispute = {
  id: number;
  state: string;
  reason: string;
  creator_user_id: number;
};

export type LinkedPayment = {
  id: number;
  amount_cents: number;
  payment_date: string;
  sender_user_id?: number | null;
  recipient_user_id?: number | null;
  payment_method: string;
};

export type Expense = {
  id: number;
  name: string;
  amount_cents: number;
  category: string;
  note?: string | null;
  purchase_date: string;
  creator_user_id: number;
  purchaser_user_id?: number | null;
  disputes?: ExpenseDispute[];
  splits?: ExpenseSplit[];
  beneficiaries?: BeneficiaryResponse[];
  attachments?: ExpenseAttachment[];
  linked_payments?: LinkedPayment[];
  is_fully_paid_back?: boolean;
};

export type ExpenseListResponse = {
  expenses: Expense[];
};

// Payment types
export type PaymentMethod = {
  payment_method_id: string;
  name: string;
  color: string;
};

export type PaymentMethodListResponse = {
  payment_methods: PaymentMethod[];
};

export type PaymentAttachment = {
  id: number;
  creator_user_id: number;
  payment_id: number;
  attachment_type: string;
  name: string;
  slug: string;
  created_at: string;
  download_url: string;
};

export type PaymentDispute = {
  id: number;
  state: string;
  reason: string;
  creator_user_id: number;
};

export type Payment = {
  id: number;
  amount_cents: number;
  note?: string | null;
  payment_date: string;
  payment_method: string;
  creator_user_id: number;
  sender_user_id?: number | null;
  recipient_user_id?: number | null;
  disputes?: PaymentDispute[];
  attachments?: PaymentAttachment[];
};

export type PaymentListResponse = {
  payments: Payment[];
};

// Unified item for transaction list
export type UnifiedItem =
  | { type: 'expense'; data: Expense }
  | { type: 'payment'; data: Payment };

// Presigned URL response for file uploads
export type PresignedUrlResponse = {
  presigned_url: string;
  fields?: Record<string, string>;
  expiration?: number;
};

// Chat/Message types
export type MessageAttachment = {
  id: number;
  download_url: string;
  attachment_type: string;
  name: string;
};

export type Message = {
  id: number;
  sender_user_id: number;
  sender_first_name: string | null;
  content_type: string;
  content: string;
  timestamp: string;
  attachments?: MessageAttachment[];
};

export type MessageListResponse = {
  messages: Message[];
  has_more: boolean;
  /** Present on some endpoints (e.g. admin household chat): total main-chat messages. */
  total_count?: number | null;
};

// Conversation types
export type MainChatPreview = {
  last_message_content: string | null;
  last_message_sender_first_name: string | null;
  last_message_timestamp: string | null;
  unread_count: number;
};

export type DisputeConversation = {
  dispute_type: 'expense' | 'payment';
  dispute_id: number;
  item_id: number;
  item_name: string;
  item_amount_cents: number;
  reason: string;
  state: string;
  is_resolved: boolean;
  created_at: string;
  creator_user_id: number;
  last_message_content: string | null;
  last_message_sender_first_name: string | null;
  last_message_timestamp: string | null;
  unread_count: number;
};

export type CustomChatPreview = {
  chat_room_id: number;
  title: string;
  creator_user_id: number;
  is_archived: boolean;
  created_at: string;
  last_message_content: string | null;
  last_message_sender_first_name: string | null;
  last_message_timestamp: string | null;
  unread_count: number;
};

export type ConversationsResponse = {
  main_chat: MainChatPreview;
  custom_chats: CustomChatPreview[];
  unresolved_disputes: DisputeConversation[];
  resolved_disputes: DisputeConversation[];
};

// Unified conversation type for selection
export type SelectedConversation =
  | { type: 'main' }
  | { type: 'expense-dispute'; disputeId: number; itemId: number }
  | { type: 'payment-dispute'; disputeId: number; itemId: number }
  | {
      type: 'custom-chat';
      chatId: number;
      title: string;
      isArchived: boolean;
      isCreator: boolean;
    };

// Calendar/Custody types
export type ScheduleType =
  | 'ALTERNATING_WEEKS'
  | '2_2_3'
  | '3_4_4_3'
  | '2_2_5_5'
  | 'EVERY_WEEKEND'
  | 'EVERY_OTHER_WEEKEND'
  | 'WEEKDAYS_ALTERNATING_WEEKENDS'
  | 'CUSTOM';

export type ExceptionStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export type CustodyAssignment = {
  beneficiary_id: number;
  beneficiary_name: string;
  assigned_user_id: number;
  assigned_user_name: string | null;
  is_exception: boolean;
  schedule_id: number | null;
  schedule_type: string | null;
  exception_status: ExceptionStatus | null;
  exception_id: number | null;
  // Transition day flags
  is_first_day_of_segment: boolean;
  is_last_day_of_segment: boolean;
  // Pickup/dropoff times from schedule (HH:MM:SS format)
  pickup_time: string | null;
  dropoff_time: string | null;
};

export type CalendarDay = {
  date: string;
  assignments: CustodyAssignment[];
  events?: EventOccurrence[];
};

export type CalendarResponse = {
  calendar: CalendarDay[];
};

export type EventOccurrence = {
  event_id: number;
  original_start_at: string;
  start_at: string;
  end_at: string | null;
  is_all_day: boolean;
  title: string;
  location: string | null;
  notes: string | null;
  timezone: string;
  beneficiary_ids: number[];
  responsible_user_id: number | null;
  is_recurring: boolean;
  is_overridden: boolean;
};

export type CalendarEvent = {
  id: number;
  title: string;
  location: string | null;
  notes: string | null;
  start_at: string;
  end_at: string | null;
  is_all_day: boolean;
  timezone: string;
  beneficiary_ids: number[];
  responsible_user_id: number | null;
  rrule: string | null;
  reminder_minutes_before: number | null;
};

export type TransitionTimeEntry = {
  time: string | null;
  on_first_day: boolean;
};

export type CustodySchedule = {
  id: number;
  beneficiary_id: number;
  creator_user_id: number;
  schedule_type: string;
  start_date: string;
  first_parent_user_id: number;
  custom_pattern: number[] | null;
  transition_times: TransitionTimeEntry[] | null;
  pickup_time: string | null;
  dropoff_time: string | null;
  created_at: string;
};

export type CustodyException = {
  id: number;
  custody_schedule_id: number;
  exception_date: string;
  assigned_user_id: number;
  reason: string | null;
  creator_user_id: number;
  status: string;
  responded_at: string | null;
  responded_by_user_id: number | null;
  pickup_time: string | null;
  dropoff_time: string | null;
  created_at: string;
};

export type CustodyExceptionListResponse = {
  exceptions: CustodyException[];
};

// Schedule type display helpers
export const SCHEDULE_TYPE_NAMES: Record<string, string> = {
  ALTERNATING_WEEKS: 'Alternating Weeks',
  '2_2_3': '2-2-3',
  '3_4_4_3': '3-4-4-3',
  '2_2_5_5': '2-2-5-5',
  EVERY_WEEKEND: 'Every Weekend',
  EVERY_OTHER_WEEKEND: 'Every Other Weekend',
  WEEKDAYS_ALTERNATING_WEEKENDS: 'Weekdays + Alternating Weekends',
  CUSTOM: 'Custom',
};

export const SCHEDULE_TYPE_DESCRIPTIONS: Record<string, string> = {
  ALTERNATING_WEEKS: 'Week on, week off (7-7)',
  '2_2_3': '2 days, 2 days, 3 days rotation',
  '3_4_4_3': '3 days, 4 days, 4 days, 3 days',
  '2_2_5_5': '2 days, 2 days, 5 days rotation',
  EVERY_WEEKEND: 'Weekdays with one, weekends with other',
  EVERY_OTHER_WEEKEND: 'Weekend visits every other week (Fri-Sun)',
  WEEKDAYS_ALTERNATING_WEEKENDS: 'Primary custody with every other weekend off',
  CUSTOM: 'Custom pattern',
};

export const SCHEDULE_OPTIONS: {
  value: ScheduleType;
  label: string;
  description: string;
}[] = [
  {
    value: 'ALTERNATING_WEEKS',
    label: 'Alternating Weeks',
    description: 'Week on, week off (7-7)',
  },
  {
    value: '2_2_3',
    label: '2-2-3',
    description: '2 days, 2 days, 3 days rotation',
  },
  {
    value: '3_4_4_3',
    label: '3-4-4-3',
    description: '3 days, 4 days, 4 days, 3 days',
  },
  {
    value: '2_2_5_5',
    label: '2-2-5-5',
    description: '2 days, 2 days, 5 days rotation',
  },
  {
    value: 'EVERY_WEEKEND',
    label: 'Every Weekend',
    description: 'Weekdays with one, weekends with other',
  },
  {
    value: 'EVERY_OTHER_WEEKEND',
    label: 'Every Other Weekend',
    description: 'Weekend visits every other week (Fri-Sun)',
  },
  {
    value: 'WEEKDAYS_ALTERNATING_WEEKENDS',
    label: 'Weekdays + Alternating Weekends',
    description: 'Primary custody with every other weekend off',
  },
];

// Schedule patterns (must match backend SCHEDULE_PATTERNS)
export const SCHEDULE_PATTERNS: Record<string, number[]> = {
  ALTERNATING_WEEKS: [0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 1, 1, 1],
  '2_2_3': [0, 0, 1, 1, 0, 0, 0, 1, 1, 0, 0, 1, 1, 1],
  '3_4_4_3': [0, 0, 0, 1, 1, 1, 1, 0, 0, 0, 0, 1, 1, 1],
  '2_2_5_5': [0, 0, 1, 1, 0, 0, 0, 0, 0, 1, 1, 1, 1, 1],
  EVERY_WEEKEND: [0, 0, 0, 0, 0, 1, 1],
  EVERY_OTHER_WEEKEND: [1, 1, 1, 1, 0, 0, 0, 1, 1, 1, 1, 1, 1, 1],
  WEEKDAYS_ALTERNATING_WEEKENDS: [0, 0, 0, 0, 1, 1, 1, 0, 0, 0, 0, 0, 0, 0],
};

export type TransitionPoint = {
  index: number;
  nextIndex: number;
  fromParent: number;
  toParent: number;
};

export function getTransitionPoints(pattern: number[]): TransitionPoint[] {
  const transitions: TransitionPoint[] = [];
  for (let i = 0; i < pattern.length; i++) {
    const nextI = (i + 1) % pattern.length;
    if (pattern[i] !== pattern[nextI]) {
      transitions.push({
        index: i,
        nextIndex: nextI,
        fromParent: pattern[i],
        toParent: pattern[nextI],
      });
    }
  }
  return transitions;
}

export type Segment = {
  parent: number;
  length: number;
  startIndex: number;
};

export function getSegments(pattern: number[]): Segment[] {
  const segments: Segment[] = [];
  if (pattern.length === 0) return segments;
  let currentParent = pattern[0];
  let startIndex = 0;
  let length = 1;
  for (let i = 1; i < pattern.length; i++) {
    if (pattern[i] === currentParent) {
      length++;
    } else {
      segments.push({ parent: currentParent, length, startIndex });
      currentParent = pattern[i];
      startIndex = i;
      length = 1;
    }
  }
  segments.push({ parent: currentParent, length, startIndex });
  return segments;
}

// Notification types
export type Notification = {
  id: number;
  created_at: string;
  user_id: number;
  household_id: number | null;
  notification_type: string;
  title: string;
  body: string;
  deeplink: string | null;
  read_at: string | null;
  data: string | null;
};

export type NotificationListResponse = {
  notifications: Notification[];
  unread_count: number;
  has_more: boolean;
};

export type UnreadCountResponse = {
  unread_count: number;
};

// Journal (private to user; API: /v1/journal)
export type JournalEntryType = 'GENERAL' | 'PHONE_CALL';

export type JournalTagBrief = {
  id: number;
  label: string;
  color: string;
  created_at: string;
};

export type JournalAttachment = {
  id: number;
  journal_entry_id: number;
  creator_user_id: number;
  file_type: string;
  name: string;
  slug: string;
  file_size: number;
  created_at: string;
};

export type JournalEntryListItem = {
  id: number;
  created_at: string;
  creator_user_id: number;
  household_id: number;
  entry_type: JournalEntryType;
  title: string | null;
  body: string | null;
  entry_date: string;
  call_type: string | null;
  call_direction: string | null;
  call_start_time: string | null;
  call_end_time: string | null;
  call_duration_minutes: number | null;
  attachments: JournalAttachment[];
  attachment_count: number | null;
  /** Present on list/detail responses that include tag joins */
  tags?: JournalTagBrief[];
};

/** GET /v1/admin/journal-feed */
export type AdminJournalFeedAuthor = {
  first_name: string | null;
  last_name: string | null;
  email: string | null;
};

export type AdminJournalFeedItem = {
  author: AdminJournalFeedAuthor;
  entry: JournalEntryListItem;
};

export type AdminJournalFeedResponse = {
  entries: AdminJournalFeedItem[];
  total: number;
};

/** GET /v1/admin/chats */
export type AdminChatType = 'main' | 'custom' | 'expense-dispute' | 'payment-dispute';

export type AdminChatListItem = {
  chat_type: AdminChatType;
  thread_id: number;
  household_id: number;
  title: string;
  subtitle: string | null;
  created_at: string;
  last_message_at: string | null;
  last_message_preview: string | null;
  last_message_sender_first_name: string | null;
  message_count: number;
  is_archived: boolean;
  is_resolved: boolean;
  member_user_ids: number[];
};

export type AdminChatsListResponse = {
  chats: AdminChatListItem[];
  total: number;
};

/** GET /v1/admin/vault/metrics */
export type AdminVaultMetricsResponse = {
  active_file_count: number;
  deleted_file_count: number;
  total_bytes: number;
  household_count: number;
  beneficiary_count: number;
  uploads_last_7_days: number;
  uploads_last_30_days: number;
};

/** GET /v1/admin/vault/files */
export type AdminVaultFileAuthor = {
  first_name: string | null;
  last_name: string | null;
  email: string | null;
};

export type AdminVaultFileRow = {
  id: number;
  created_at: string;
  deleted_at: string | null;
  creator_user_id: number;
  author: AdminVaultFileAuthor;
  beneficiary_id: number;
  beneficiary_name: string;
  household_id: number;
  file_type: string;
  name: string;
  file_size: number;
};

export type AdminVaultFilesListResponse = {
  files: AdminVaultFileRow[];
  total: number;
};

// Exchange compliance summary (GET /v1/exchange-logs/compliance)
export type ExchangeComplianceSummary = {
  date_range: { start: string; end: string };
  beneficiary: { id: number; name: string } | null;
  total_logged: number;
  by_status: {
    ON_TIME: number;
    LATE: number;
    NO_SHOW: number;
    CANCELLED: number;
  };
  avg_minutes_late: number | null;
  total_minutes_late: number | null;
  on_time_pct: number | null;
};

// GPS check-ins
export type CheckIn = {
  id: number;
  created_at: string;
  creator_user_id: number;
  household_id: number;
  latitude: number;
  longitude: number;
  address: string | null;
  note: string | null;
};

export type CheckInListResponse = {
  check_ins: CheckIn[];
};

// Vault types
export type VaultFile = {
  id: number;
  creator_user_id: number;
  beneficiary_id: number;
  file_type: string;
  name: string;
  slug: string;
  file_size: number;
  created_at: string;
};

export type DeleteVaultFileResponse = {
  file_id: number;
};

export interface AdminUserRow {
  id: number;
  created_at: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone_number: string | null;
  /** Email-only sign-up with no phone (matches Pro web flow) */
  is_professional: boolean;
  calm_filter_enabled: boolean;
  subscribed: boolean;
  subscription_override: boolean;
  deleted_at: string | null;
  /** Non-dispute chat messages across all households this user belongs to */
  household_message_count: number;
  /** Non-deleted expenses across those households */
  household_expense_count: number;
}

export interface AdminUsersResponse {
  users: AdminUserRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface AdminDashboardMetricsResponse {
  expense_count: number;
  expense_amount_cents: number;
  journal_entry_count: number;
  message_count: number;
  total_user_count: number;
  subscribed_user_count: number;
  professional_user_count: number;
  professional_invite_pending_count: number;
  professional_invite_accepted_count: number;
}

export interface AdminCoachEngagementCadenceStep {
  email_key: string;
  delay_days: number;
  subject: string;
  template: string;
}

export interface AdminCoachEngagementUpcomingRow {
  user_id: number;
  professional_email: string;
  professional_name: string | null;
  created_at: string;
  reference_date: string;
  days_since_reference: number;
  next_email_key: string;
  sent_keys: string[];
  available_code_count: number;
  accepted_client_count: number;
}

export interface AdminCoachEngagementNotQueuedRow {
  user_id: number;
  professional_email: string;
  professional_name: string | null;
  created_at: string;
  reference_date: string;
  days_since_reference: number;
  reason: string;
  next_email_key: string | null;
  days_until_next_email: number | null;
  eligible_at_utc: string | null;
  sent_keys: string[];
  available_code_count: number;
  accepted_client_count: number;
}

export interface AdminCoachEngagementRecentSendRow {
  id: number;
  user_id: number;
  professional_email: string;
  professional_name: string | null;
  email_key: string;
  sent_at: string;
  mailgun_message_id: string | null;
}

export interface AdminCoachEngagementStatusResponse {
  campaign_name: string;
  schedule_environment: string;
  schedule_description: string;
  next_run_at_utc: string | null;
  next_run_in_seconds: number | null;
  professionals_total: number;
  professionals_actionable: number;
  next_run_will_send_count: number;
  upcoming_sends: AdminCoachEngagementUpcomingRow[];
  pending_not_queued: AdminCoachEngagementNotQueuedRow[];
  cadence: AdminCoachEngagementCadenceStep[];
  sends_last_7_days: number;
  sends_last_30_days: number;
  sends_all_time: number;
  skipped_invalid_recipient_all_time: number;
  unsubscribed_global_count: number;
  unsubscribed_campaign_count: number;
  email_suppressed_user_count: number;
}

export interface PulseDaySummary {
  users_registered: number;
  households_created: number;
  professional_invites: number;
  messages_created: number;
  expenses_created: number;
  payments_created: number;
  journal_entries_created: number;
  active_users: number;
  audit_events: number;
}

export interface PulseEventTypeCount {
  event_type: string;
  count: number;
}

export interface PulseNewUserRow {
  id: number;
  created_at: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone_number: string | null;
}

export interface PulseAuditImage {
  source: 'message_attachment' | 'journal_attachment';
  attachment_id: number;
  download_url: string;
  file_type: string;
}

export interface PulseAuditFeedItem {
  id: number;
  created_at: string;
  event_type: string;
  entity_type: string;
  entity_id: number;
  actor_user_id: number | null;
  household_id: number | null;
  detail: string | null;
  images?: PulseAuditImage[];
}

export interface PulseAuditUserBucket {
  actor_user_id: number | null;
  display_name: string | null;
  email: string | null;
  phone_number: string | null;
  event_count: number;
  events: PulseAuditFeedItem[];
}

export interface AdminPulseDayResponse {
  date: string;
  summary: PulseDaySummary;
  audit_by_event_type: PulseEventTypeCount[];
  audit_by_user: PulseAuditUserBucket[];
  new_users: PulseNewUserRow[];
}

export interface AdminProspectTierCount {
  tier: number | null;
  count: number;
}

export interface AdminProspectSequenceCount {
  email_key: string;
  prospects_reached: number;
}

export interface AdminProspectEmailKeyEngagement {
  email_key: string;
  sends: number;
  opens: number;
}

export interface AdminProspectVariantStats {
  variant: string;
  sends: number;
  opens: number;
}

export interface AdminProspectsSummaryResponse {
  campaign_paused: boolean;
  campaign_sending_enabled: boolean;
  campaign_name: string;
  active_variant_keys: string[];
  email_sequence_keys: string[];
  total_prospects: number;
  prospects_with_email: number;
  prospects_active: number;
  prospects_unsubscribed: number;
  unsubscribed_rate_of_listable: number;
  tier_counts: AdminProspectTierCount[];
  total_sends: number;
  sends_last_7d: number;
  sends_last_30d: number;
  total_opens: number;
  open_rate_total: number;
  sequence: AdminProspectSequenceCount[];
  engagement_by_email_key: AdminProspectEmailKeyEngagement[];
  variant_stats: AdminProspectVariantStats[];
  likely_signed_up_count: number;
  prospective_users_count: number;
}

export interface AdminProspectListRow {
  id: number;
  email: string | null;
  tier: number | null;
  variant: string | null;
  unsubscribed_at: string | null;
  created_at: string;
  campaign_sends_count: number;
  last_campaign_sent_at: string | null;
  /** Non-deleted user with same email (lowest id); null if no match. */
  matched_user_id: number | null;
  matched_user_label: string | null;
}

export interface AdminProspectsListResponse {
  rows: AdminProspectListRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface PreWhitelistedNumberAdminItem {
  id: number;
  phone_number: string;
  created_at: string;
  applied: boolean;
  applied_at: string | null;
  applied_to_user_id: number | null;
}

export interface PreWhitelistedNumberListResponse {
  items: PreWhitelistedNumberAdminItem[];
}

export interface MarketingEmailSuppressionAdminItem {
  id: number;
  email: string;
  reason: string | null;
  created_at: string;
}

export interface MarketingEmailSuppressionListResponse {
  items: MarketingEmailSuppressionAdminItem[];
}

export interface SendPushNotificationResponse {
  success: boolean;
  users_notified: number;
  message: string;
}

/** POST /v1/admin/send-test-resend-email — admin dashboard Resend smoke test */
export interface SendTestResendEmailResponse {
  success: boolean;
  message: string;
  resend_message_id: string | null;
}

export type DeliverabilityTestProvider = 'resend' | 'mailgun';

/** POST /v1/admin/send-deliverability-test-email — DMARC/SPF/DKIM smoke test */
export interface SendDeliverabilityTestEmailResponse {
  success: boolean;
  provider: DeliverabilityTestProvider;
  message: string;
  from_email: string;
  message_id: string | null;
}

export interface AdminHouseholdMemberRow {
  user_id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone_number: string | null;
  is_target: boolean;
}

export interface AdminBeneficiaryRow {
  id: number;
  created_at: string;
  type: string;
  name: string;
  birthdate: string | null;
}

export interface AdminHouseholdSummary {
  household_id: number;
  created_at: string;
  members: AdminHouseholdMemberRow[];
  message_count: number;
  expense_count: number;
  beneficiaries: AdminBeneficiaryRow[];
}

export interface AdminUserAggregates {
  messages_authored: number;
  expenses_authored: number;
  payments_authored: number;
  journal_entries_authored: number;
  check_ins_authored: number;
}

export interface AdminAttributionSummary {
  source: string | null;
  medium: string | null;
  campaign: string | null;
  gclid: string | null;
  captured_at: string | null;
}

export interface AdminOnboardingSummary {
  completed_at: string | null;
  answers: Record<string, unknown> | null;
}

export interface AdminProfessionalInviteSummary {
  id: number;
  household_id: number;
  coparent_user_id: number;
  professional_email: string;
  professional_user_id: number | null;
  status: string;
}

export interface AdminTrialGrantSummary {
  granted_at: string;
  expires_at: string;
  source: string;
  converted: boolean;
  is_active: boolean;
}

export interface AdminUserDetailResponse {
  id: number;
  clerk_id: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone_number: string | null;
  date_of_birth: string | null;
  timezone: string | null;
  created_at: string;
  deleted_at: string | null;
  subscribed: boolean;
  subscription_override: boolean;
  revenuecat_subscription_status: string | null;
  stripe_subscription_status: string | null;
  has_push_token: boolean;
  calm_filter_enabled: boolean;
  calm_filter_quiet_start_minutes: number | null;
  calm_filter_quiet_end_minutes: number | null;
  calm_filter_active_now: boolean;
  calm_filter_quiet_end_at_utc: string | null;
  calm_filter_last_digest_at: string | null;
  aggregates: AdminUserAggregates;
  households: AdminHouseholdSummary[];
  attribution: AdminAttributionSummary | null;
  onboarding: AdminOnboardingSummary | null;
  professional_invites: AdminProfessionalInviteSummary[];
  trial_grant: AdminTrialGrantSummary | null;
}

export interface AdminAuditLogRow {
  id: number;
  created_at: string;
  event_type: string;
  entity_type: string;
  entity_id: number;
  actor_user_id: number | null;
  household_id: number | null;
  ip_address: string;
  user_agent: string | null;
  event_data: Record<string, unknown> | null;
  changes: Record<string, unknown> | null;
}

export interface AdminAuditLogListResponse {
  rows: AdminAuditLogRow[];
  has_more: boolean;
}

export interface AdminHouseholdExpensesResponse {
  expenses: ExpenseListResponse;
  payments: PaymentListResponse;
  accounting: AccountingSummary | null;
}

export interface SendDripEmailUserResult {
  user_id: number;
  success: boolean;
  message: string;
}

export interface SendDripEmailResponse {
  success: boolean;
  message: string;
  results: SendDripEmailUserResult[];
}

/** GET /v1/admin/drip-campaigns/catalog */
export interface AdminDripCatalogStep {
  key: string;
  channel: 'email' | 'push' | 'sms';
  delay_days: number | null;
  delay_hours: number | null;
  template: string | null;
}

export interface AdminDripCatalogEntry {
  id: string;
  name: string;
  goal: 'activation' | 'conversion' | 'retention' | 'growth' | 'prospects';
  channels: ('email' | 'push' | 'sms')[];
  send_tables: string[];
  tracking_enabled: boolean;
  enabled: boolean;
  eligibility_description: string;
  beat_keys: string[];
  steps: AdminDripCatalogStep[];
  notes: string | null;
  keyed_by: 'user_id' | 'invite_id' | 'prospect_id';
}

export interface AdminDripCatalogResponse {
  campaigns: AdminDripCatalogEntry[];
}

export interface AdminDripSummaryRow {
  id: string;
  sends: number;
  opens: number | null;
  open_rate: number | null;
  eligible_now: number | null;
  conversions: number;
  /** conversions / sends in range; null when sends === 0 */
  conversion_rate: number | null;
}

export interface AdminDripSummaryResponse {
  range: '7d' | '30d' | '90d' | 'all';
  totals: {
    sends: number;
    opens: number;
    conversions: number;
    /** sum(conversions) / sum(sends) */
    conversion_rate: number | null;
  };
  per_campaign: AdminDripSummaryRow[];
}

export interface AdminDripDailySend {
  date: string;
  step_key: string;
  channel: string;
  sends: number;
}

export interface AdminDripStepRow {
  step_key: string;
  channel: string;
  sends: number;
  opens: number | null;
  open_rate: number | null;
}

export interface AdminDripMetricsResponse {
  id: string;
  range: '7d' | '30d' | '90d' | 'all';
  eligible_now: number | null;
  daily_sends: AdminDripDailySend[];
  step_breakdown: AdminDripStepRow[];
  total_sends: number;
  total_opens: number | null;
  open_rate: number | null;
  conversions: number;
  /** conversions / total_sends; null when total_sends === 0 */
  conversion_rate: number | null;
}

export interface AdminDripSendRow {
  id: number;
  sent_at: string;
  channel: string;
  step_key: string;
  user_id: number | null;
  invite_id: number | null;
  prospect_id: number | null;
  user_label: string | null;
  opened_at: string | null;
  mailgun_message_id: string | null;
}

export interface AdminDripSendsResponse {
  rows: AdminDripSendRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface AdminDripEligibilityResponse {
  id: string;
  eligible_now: number | null;
  description: string;
}

export interface AdminDripScheduleRow {
  beat_key: string;
  task: string;
  schedule_human: string;
  last_send_at: string | null;
}

/** GET /v1/admin/google-ads/metrics?days=N */
export interface GoogleAdsTotals {
  impressions: number;
  clicks: number;
  cost_micros: number;
  conversions: number;
  conversions_value: number;
  ctr: number | null;
  avg_cpc_micros: number | null;
  conversion_rate: number | null;
  cost_per_conversion_micros: number | null;
}

export interface GoogleAdsDailyPoint {
  date: string; // YYYY-MM-DD
  impressions: number;
  clicks: number;
  cost_micros: number;
  conversions: number;
  conversions_value: number;
}

export interface GoogleAdsCampaignRow {
  id: string;
  name: string;
  status: string;
  advertising_channel_type: string | null;
  impressions: number;
  clicks: number;
  cost_micros: number;
  conversions: number;
  conversions_value: number;
}

export interface AdminGoogleAdsMetricsResponse {
  ok: boolean;
  days: number;
  start_date: string;
  end_date: string;
  customer_id_masked: string;
  login_customer_id_masked: string;
  currency_code: string | null;
  time_zone: string | null;
  env_vars: Record<string, boolean>;
  error: string | null;
  totals: GoogleAdsTotals;
  daily: GoogleAdsDailyPoint[];
  campaigns: GoogleAdsCampaignRow[];
}

/** POST /v1/admin/check-google-ads-credentials */
export interface AdminCheckGoogleAdsCredentialsResponse {
  ok: boolean;
  reason: string | null;
  step: string | null;
  error_code: string | null;
  message: string | null;
  env_vars: Record<string, boolean>;
  alerted: boolean;
  dedup_suppressed: boolean;
}

/** POST /v1/admin/backfill-message-audit-events */
export interface BackfillMessageAuditRequest {
  dry_run: boolean;
  limit: number;
}

export interface BackfillMessageAuditResponse {
  dry_run: boolean;
  missing_before: number;
  created: number;
  sample_message_ids: number[];
}
